/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * Découpage Pro - Canonical Schema & Validation
 * Professional cinema shotlist with timecode, camera lock, and character injection.
 */
import { z } from 'zod';
import { Dogma } from '../types';

// === TIMECODE UTILITIES ===

/**
 * Convert timecode string to frame count
 * @param tc Timecode in format HH:MM:SS:FF
 * @param fps Frames per second
 */
export function tcToFrames(tc: string, fps: number): number {
    const [h, m, s, f] = tc.split(':').map(Number);
    return h * 3600 * fps + m * 60 * fps + s * fps + f;
}

/**
 * Convert frame count to timecode string
 * @param frames Total frame count
 * @param fps Frames per second
 */
export function framesToTc(frames: number, fps: number): string {
    const fpsInt = Math.floor(fps); // Use integer for frame count
    const f = frames % fpsInt;
    const totalSec = Math.floor(frames / fpsInt);
    const s = totalSec % 60;
    const totalMin = Math.floor(totalSec / 60);
    const m = totalMin % 60;
    const h = Math.floor(totalMin / 60);
    return [h, m, s, f].map(n => n.toString().padStart(2, '0')).join(':');
}

/**
 * Calculate duration in seconds between two timecodes
 */
export function tcDurationSec(tcIn: string, tcOut: string, fps: number): number {
    return (tcToFrames(tcOut, fps) - tcToFrames(tcIn, fps)) / fps;
}

/**
 * Validate timecode format
 */
export function isValidTimecode(tc: string): boolean {
    return /^\d{2}:\d{2}:\d{2}:\d{2}$/.test(tc);
}

/**
 * Validate timecode frames < fps
 */
export function isValidTimecodeFrames(tc: string, fpsTimecode: number): boolean {
    const frames = parseInt(tc.split(':')[3], 10);
    return frames < fpsTimecode;
}

/**
 * Validate tc_out > tc_in with project fps
 */
export function validateTimecodeOrder(tcIn: string, tcOut: string, fps: number): boolean {
    return tcToFrames(tcOut, fps) > tcToFrames(tcIn, fps);
}

/**
 * Ripple timecodes across subsequent shots.
 * Recalculates tc_in/tc_out for all shots after startIndex.
 */
export function rippleShots(shots: ShotSpec[], startIndex: number, fps: number): ShotSpec[] {
    const results = [...shots];
    for (let i = startIndex + 1; i < results.length; i++) {
        const prev = results[i - 1];
        const current = results[i];

        const durationFrames = tcToFrames(current.tc_out, fps) - tcToFrames(current.tc_in, fps);
        const newInFrames = tcToFrames(prev.tc_out, fps);

        results[i] = {
            ...current,
            tc_in: framesToTc(newInFrames, fps),
            tc_out: framesToTc(newInFrames + durationFrames, fps),
            updated_at: new Date().toISOString()
        };
    }
    return results;
}


// Supported timecode FPS (integer only - 23.976 uses 24 for TC display)
export const TIMECODE_FPS_OPTIONS = [24, 25, 30, 50, 60] as const;
export type TimecodeFps = typeof TIMECODE_FPS_OPTIONS[number];

// === ENUMS & CONSTANTS ===

export const SHOT_SCALES = [
    'plan_ensemble',
    'demi_ensemble',
    'plan_large',
    'plan_moyen',
    'plan_americain',
    'plan_rapproche',
    'plan_poitrine',
    'plan_epaule',
    'gros_plan',
    'tres_gros_plan',
    'insert',
] as const;

export const SHOT_ANGLES = [
    'plongee',
    'contre_plongee',
    'neutre',
    'dutch',
] as const;

export const SHOT_MOVEMENTS = [
    'fixe',
    'travelling_avant',
    'travelling_arriere',
    'travelling_lateral',
    'panoramique',
    'tilt_up',
    'tilt_down',
    'steadicam',
    'dolly_zoom',
    'crane',
    'handheld',
    'arc',
    'orbit',
] as const;

export const SCREEN_DIRECTIONS = [
    'left_to_right',
    'right_to_left',
    'neutral',
] as const;

export const FRAMINGS = [
    'center',
    'rule_of_thirds',
    'over_shoulder',
    'two_shot',
] as const;


export const SHOT_STATUSES = [
    'pending',
    'generating',
    'candidates_ready',
    'locked',
    'video_generating',
    'video_ready',
] as const;

// === ZOD SCHEMAS ===

/**
 * Camera specification with lock capability
 * If locked=true, Gemini/LLM cannot modify these values
 */
export const CameraSpecSchema = z.object({
    scale: z.enum(SHOT_SCALES),
    axis_deg: z.number().min(-180).max(180).default(0),
    angle: z.enum(SHOT_ANGLES).default('neutre'),
    lens_mm: z.number().min(8).max(300).default(35),
    height_m: z.number().min(0.1).max(10).optional(),
    movement: z.enum(SHOT_MOVEMENTS).default('fixe'),
    screen_direction: z.enum(SCREEN_DIRECTIONS).optional(),
    framing: z.enum(FRAMINGS).optional(), // For OTS shots
    locked: z.boolean().default(true),
});

/**
 * Character reference with image injection
 * NOTE: character_id is optional - app resolves it from name
 */
export const CharacterRefSchema = z.object({
    name: z.string().min(1),
    character_id: z.string().optional(), // App fills this, not Gemini
    refs: z.array(z.enum(['front', 'profile', 'back'])).default(['front']),
    identity_lock: z.enum(['soft', 'hard']).default('hard'),
});

/**
 * Image provider configuration per shot
 */
export const ImageProviderSchema = z.object({
    engine: z.string().default('nano_banana_pro'), // Dynamic from Registry, default to Nano
    candidates: z.number().min(1).max(12).default(8),
    seed: z.number().int().optional(),
    quality: z.enum(['draft', 'final']).default('draft'),
});

/**
 * Deliverables configuration
 */
export const DeliverablesSchema = z.object({
    stills: z.array(z.enum(['start', 'hero', 'end'])).default(['hero']),
    ratios: z.array(z.enum(['16:9', '9:16'])).default(['16:9']),
});

/**
 * Shot candidate (generated image option)
 */
export const ShotCandidateSchema = z.object({
    id: z.string(),
    shot_id: z.string(),
    engine: z.string(), // Dynamic
    image_url: z.string().url().optional(),
    image_base64: z.string().optional(),
    seed: z.number().int().optional(),
    meta: z.record(z.string(), z.any()).optional(),
    created_at: z.string(),
});

/**
 * Single shot specification
 * The core unit of the découpage system
 * NOTE: id is optional - app generates it via ensureShotIds()
 * NOTE: tc_out > tc_in validation is done at project level with fps context
 */
export const ShotSpecSchema = z.object({
    // ID is optional - app generates it, not Gemini
    id: z.string().optional(),
    number: z.number().int().min(1).optional(),

    // Timecode (strict, calculated by system NOT LLM)
    tc_in: z.string().regex(/^\d{2}:\d{2}:\d{2}:\d{2}$/, 'Invalid timecode format'),
    tc_out: z.string().regex(/^\d{2}:\d{2}:\d{2}:\d{2}$/, 'Invalid timecode format'),

    // Camera (locked values)
    camera: CameraSpecSchema,

    // Characters with image refs - ONLY chars in this list get injected
    characters: z.array(CharacterRefSchema).optional(),

    // Image generation settings
    image_provider: ImageProviderSchema,

    // Output settings
    deliverables: DeliverablesSchema,

    // Creative content (LLM can fill these)
    prompt_notes: z.string().default(''),
    action: z.string().optional(),
    negative_prompt: z.string().optional(),

    // State
    status: z.enum(SHOT_STATUSES).default('pending'),
    locked_candidate_id: z.string().optional(), // App generates, not Gemini
    locked_candidate_base64: z.string().optional(), // Persist the actual image base64

    // Dogma binding
    dogma_id: z.string().optional(),

    // Audit
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
});

/**
 * Project specification for Découpage Pro mode
 */
export const ProjectSpecSchema = z.object({
    id: z.string().optional(), // App generates
    mode: z.literal('decoupage'),

    // Project settings - fps for timecode (integer)
    fps: z.number().int().min(24).max(60).default(25),
    // Render fps can be float (23.976)
    fps_render: z.number().min(23.976).max(60).optional(),
    aspect_default: z.enum(['16:9', '9:16']).default('16:9'),

    // Optional master shot (establishing shot for reference)
    master_shot: ShotSpecSchema.optional(),

    // All shots in the project
    shots: z.array(ShotSpecSchema),

    // Dogma binding for whole project
    dogma_id: z.string().optional(),

    // Audit
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
}).refine(
    (data) => {
        // Validate all shots have tc_out > tc_in using project fps
        for (const shot of data.shots) {
            if (!validateTimecodeOrder(shot.tc_in, shot.tc_out, data.fps)) {
                return false;
            }
            // Validate frames < fps
            if (!isValidTimecodeFrames(shot.tc_in, data.fps) ||
                !isValidTimecodeFrames(shot.tc_out, data.fps)) {
                return false;
            }
        }
        return true;
    },
    { message: 'All shots must have valid timecodes (tc_out > tc_in, frames < fps)' }
);

// === INFERRED TYPES ===

export type CameraSpec = z.infer<typeof CameraSpecSchema>;
export type CharacterRef = z.infer<typeof CharacterRefSchema>;
export type ImageProvider = z.infer<typeof ImageProviderSchema>;
export type Deliverables = z.infer<typeof DeliverablesSchema>;
export type ShotCandidate = z.infer<typeof ShotCandidateSchema>;
export type ShotSpec = z.infer<typeof ShotSpecSchema>;
export type ProjectSpec = z.infer<typeof ProjectSpecSchema>;

export type ShotScale = typeof SHOT_SCALES[number];
export type ShotAngle = typeof SHOT_ANGLES[number];
export type ShotMovement = typeof SHOT_MOVEMENTS[number];
export type ImageEngine = string;
export type ShotStatus = typeof SHOT_STATUSES[number];
export type Framing = typeof FRAMINGS[number];

// === VALIDATION HELPERS ===

/**
 * Ensure all shots have IDs (generates missing ones)
 */
export function ensureShotIds(shots: ShotSpec[]): ShotSpec[] {
    return shots.map((shot, index) => ({
        ...shot,
        id: shot.id || crypto.randomUUID(),
        number: shot.number || index + 1,
    }));
}

/**
 * Resolve character IDs from name lookup
 */
export function resolveCharacterIds(
    shot: ShotSpec,
    characters: { id: string; name: string }[]
): ShotSpec {
    if (!shot.characters) return shot;

    return {
        ...shot,
        characters: shot.characters.map(charRef => {
            if (charRef.character_id) return charRef;
            const found = characters.find(
                c => c.name.toLowerCase() === charRef.name.toLowerCase()
            );
            return {
                ...charRef,
                character_id: found?.id,
            };
        }),
    };
}

/**
 * Validate a shot spec and return errors if any
 */
export function validateShotSpec(data: unknown): {
    success: boolean;
    data?: ShotSpec;
    errors?: z.ZodError
} {
    const result = ShotSpecSchema.safeParse(data);
    if (result.success) {
        return { success: true, data: result.data };
    }
    return { success: false, errors: result.error };
}

/**
 * Validate a project spec and return errors if any
 */
export function validateProjectSpec(data: unknown): {
    success: boolean;
    data?: ProjectSpec;
    errors?: z.ZodError;
} {
    const result = ProjectSpecSchema.safeParse(data);
    if (result.success) {
        return { success: true, data: result.data };
    }
    return { success: false, errors: result.error };
}

/**
 * Create a new empty shot with default values
 */
export function createEmptyShot(tcIn: string = '00:00:00:00', durationSec: number = 3, fps: number = 25): ShotSpec {
    const framesIn = tcToFrames(tcIn, fps);
    const framesOut = framesIn + Math.round(durationSec * fps);
    const tcOut = framesToTc(framesOut, fps);

    return {
        id: crypto.randomUUID(),
        tc_in: tcIn,
        tc_out: tcOut,
        camera: {
            scale: 'plan_moyen',
            axis_deg: 0,
            angle: 'neutre',
            lens_mm: 35,
            movement: 'fixe',
            locked: true,
        },
        image_provider: {
            engine: 'nano_banana_pro',
            candidates: 8,
            quality: 'draft',
        },
        deliverables: {
            stills: ['hero'],
            ratios: ['16:9'],
        },
        prompt_notes: '',
        status: 'pending',
    };
}

/**
 * Calculate the next available tc_in based on previous shot
 */
export function getNextTcIn(shots: ShotSpec[]): string {
    if (shots.length === 0) return '00:00:00:00';
    const lastShot = shots[shots.length - 1];
    return lastShot.tc_out;
}

// === DISPLAY HELPERS ===

export const SCALE_DISPLAY_NAMES: Record<ShotScale, string> = {
    plan_ensemble: 'Plan d\'ensemble',
    demi_ensemble: 'Demi-ensemble',
    plan_large: 'Plan large',
    plan_moyen: 'Plan moyen',
    plan_americain: 'Plan américain',
    plan_rapproche: 'Plan rapproché',
    plan_poitrine: 'Plan poitrine',
    plan_epaule: 'Plan épaule',
    gros_plan: 'Gros plan',
    tres_gros_plan: 'Très gros plan',
    insert: 'Insert',
};

export const ANGLE_DISPLAY_NAMES: Record<ShotAngle, string> = {
    plongee: 'Plongée',
    contre_plongee: 'Contre-plongée',
    neutre: 'Neutre',
    dutch: 'Dutch angle',
};

export const MOVEMENT_DISPLAY_NAMES: Record<ShotMovement, string> = {
    fixe: 'Fixe',
    travelling_avant: 'Travelling avant',
    travelling_arriere: 'Travelling arrière',
    travelling_lateral: 'Travelling latéral',
    panoramique: 'Panoramique',
    tilt_up: 'Tilt up',
    tilt_down: 'Tilt down',
    steadicam: 'Steadicam',
    dolly_zoom: 'Dolly zoom',
    crane: 'Grue',
    handheld: 'Caméra épaule',
    arc: 'Arc',
    orbit: 'Orbit',
};

export const ENGINE_DISPLAY_NAMES: Record<ImageEngine, string> = {
    nano_banana_pro: 'Nano Banana Pro',
    flux: 'Flux',
    ltx2_image: 'LTX-2 Image',

};

export const FRAMING_DISPLAY_NAMES: Record<Framing, string> = {
    center: 'Centré',
    rule_of_thirds: 'Règle des tiers',
    over_shoulder: 'Par-dessus épaule',
    two_shot: 'Plan deux',
};

/**
 * Compose a deterministic prompt for image generation engines
 * Combines shot specs, dogma context, and camera details.
 */
export function composeShotPrompt(shot: ShotSpec, dogma: Dogma | null): string {
    const parts: string[] = [];

    // 1. Dogma context (if available)
    if (dogma?.text) {
        // Use a concise style prefix
        parts.push(`Cinematic style: ${dogma.text}`);
    }

    // 2. Camera details
    const scale = SCALE_DISPLAY_NAMES[shot.camera.scale as ShotScale] || shot.camera.scale;
    const angle = ANGLE_DISPLAY_NAMES[shot.camera.angle as ShotAngle] || shot.camera.angle;
    const movement = MOVEMENT_DISPLAY_NAMES[shot.camera.movement as ShotMovement] || shot.camera.movement;

    const cameraDesc = `${scale}, ${angle} angle, ${movement} shot`;
    parts.push(cameraDesc);

    if (shot.camera.lens_mm) parts.push(`${shot.camera.lens_mm}mm lens`);

    // 3. Action & Notes
    if (shot.action) parts.push(shot.action);
    if (shot.prompt_notes) parts.push(shot.prompt_notes);

    // 4. Character mentions
    if (shot.characters && shot.characters.length > 0) {
        const names = shot.characters.map(c => `@${c.name}`).join(', ');
        parts.push(`Featuring characters: ${names}`);
    }

    return parts.join('. ').replace(/\. \./g, '.').trim();
}

